import React, { useState, useEffect, useContext } from "react";
import moment from "moment";
import {
  Card,
  CardHeader,
  CardBody,
  Container,
  Row,
  Col,
  Badge,
  Label,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import AdminHeader from "components/Headers/AdminHeader";
import EnrolledStudentsTab from "../Courses/EnrolledStudentsTab";
import CourseArtifacts from "../Courses/CourseArtifacts";
import {
  urlDecodedId,
  urlEncodedId,
} from "components/CommonComponents/encodeDecodeFunction";
import { getBatchById } from "action/batch";
import { toTitleCase } from "components/CommonComponents/CommonFunctions";
import DetailsHeader from "components/CommonComponents/DetailsHeader";
import InfoPopover from "components/CommonComponents/InfoPopover";
import DOMPurify from "dompurify";
import { PREVIEW_LIMIT } from "constants/CommonConstants";
import { STATUS_BADGE_COLORS } from "components/constant";
import { NotificationContext } from "components/CommonComponents/NotificationProvider";

function BatchDetails() {
  const authData = JSON.parse(localStorage.getItem("p") || "{}");
  const role = authData?.role;

  const assessmentArtifactListData = useSelector(
    (state) => state.assessment?.assessmentArtifactList
  );

  const { notify } = useContext(NotificationContext);

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [batchDetailData, setBatchDetailData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    description: false,
    scheduleInfo: false,
  });

  const [activeTab, setActiveTab] = useState("details");
  const [showArtifacts, setShowArtifacts] = useState(false);
  const [artifactProps, setArtifactProps] = useState(null);
  const [copiedLink, setCopiedLink] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);

  const handleViewArtifacts = (traineeRow) => {
    setArtifactProps(traineeRow);
    setShowArtifacts(true);

    const params = new URLSearchParams(location.search);
    params.set("tab", "artifacts");

    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
      state: {
        ...location.state,
        traineeID: traineeRow?.traineeID || traineeRow?.traineeId,
      },
    });
  };

  const handleBackFromArtifacts = () => {
    const params = new URLSearchParams(location.search);
    params.set("tab", "enrollment");

    const newState = { ...location.state };
    delete newState.traineeID;

    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true,
      state: newState,
    });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab") || "details";

    setActiveTab(tab);

    if (tab === "artifacts") {
      setShowArtifacts(true);
      setArtifactProps({
        traineeID: location.state?.traineeID,
      });
    } else {
      setShowArtifacts(false);
      setArtifactProps(null);
    }
  }, [location.search, location.state]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  };

  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  const decodedId = id ? urlDecodedId(id) : null;

  const showEnrollmentTab = role === "TRAINER" || "ADMIN";
  const hideForTrainer = role === "TRAINER";

  useEffect(() => {
    if (decodedId) {
      getBatchDetail(decodedId);
    } else {
      setShowAlternative({
        loader: false,
        error: "Invalid batch ID",
      });
    }
  }, [decodedId]);

  const getBatchDetail = async (batchId) => {
    try {
      setShowAlternative({
        loader: true,
        error: "",
      });

      const res = await getBatchById(batchId);

      if (res?.code === 200) {
        setBatchDetailData(res?.data);

        setShowAlternative({
          loader: false,
          error: "",
        });
      } else {
        setShowAlternative({
          loader: false,
          error: res?.message || "Failed to load Batch details",
        });
      }
    } catch (error) {
      setShowAlternative({
        loader: false,
        error: "Something went wrong while loading batch details",
      });
    }
  };

  const formatDate = (date) => {
    if (!date) return "—";

    const parsedDate = new Date(date);

    if (isNaN(parsedDate.getTime())) return date;

    return parsedDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // const getStatusColor = (status) => {
  //   switch (status) {
  //     case "ACTIVE":
  //       return "success";
  //     case "CANCELLED":
  //       return "danger";
  //     case "COMPLETED":
  //       return "info";
  //     case "INACTIVE":
  //       return "warning";
  //     default:
  //       return "secondary";
  //   }
  // };

  const handleBack = () => {
    if (location.state?.fromCourseSchedule) {
      navigate(-1);
    } else {
      navigate(
        role === "TRAINER"
          ? "/instructor/batch"
          : role === "SUPER_ADMIN"
            ? "/super-admin/batch"
            : "/admin/batch",
      );
    }
  };

  const handleEdit = () => {
    navigate(
      role === "SUPER_ADMIN"
        ? `/super-admin/batch/edit/${id}`
        : role === "CHANNEL_PARTNER"
          ? `/channel-partner/batch/edit/${id}`
          : `/admin/batch/edit/${id}`,
      {
        state: {
          mode: "edit",
          fromCourseSchedule: location.state?.fromCourseSchedule,
        },
      },
    );
  };

  const truncateHtml = (html, limit) => {
    const container = document.createElement("div");
    container.innerHTML = html;
    let count = 0;
    const walk = (node) => {
      if (count >= limit) {
        node.parentNode?.removeChild(node);
        return;
      }
      if (node.nodeType === Node.TEXT_NODE) {
        const remaining = limit - count;
        if (node.textContent.length > remaining) {
          node.textContent = node.textContent.slice(0, remaining) + "...";
          count = limit;
        } else {
          count += node.textContent.length;
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        Array.from(node.childNodes).forEach(walk);
      }
    };
    Array.from(container.childNodes).forEach(walk);
    return container.innerHTML;
  };

  const renderExpandableDescription = (
    html,
    isExpanded,
    onToggle,
    className = "course-content",
  ) => {
    const sanitized = DOMPurify.sanitize(
      html || "<p>No information available.</p>",
    );

    const el = document.createElement("div");
    el.innerHTML = sanitized;
    el.querySelectorAll('h1, h2, h3, h4, h5, h6, p, br, li').forEach(l => {
      l.insertAdjacentText('afterend', '\n');
    });

    const plainText = el.textContent || el.innerText || "";
    const isLong = plainText.length > PREVIEW_LIMIT;

    return (
      <>
        <div
          className={className}
          style={{ whiteSpace: "pre-wrap" }}
        >
          {isExpanded || !isLong
            ? plainText
            : plainText.slice(0, PREVIEW_LIMIT) + "..."}
        </div>

        {isLong && (
          <div className="text-center mt-2">
            <button
              className="lesson-read-more-btn"
              onClick={() => onToggle(!isExpanded)}
            >
              {isExpanded ? "Read Less" : "Read More"}
            </button>
          </div>
        )}
      </>
    );
  };

  const isBatchStarted = batchDetailData?.batchStartDate
    ? moment(batchDetailData.batchStartDate, "DD MMM YYYY").isSameOrBefore(
      moment(),
      "day",
    )
    : false;

  const handleCopyEnrollmentLink = async (url, batchId, type) => {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);

      setCopiedLink({
        batchId,
        type,
      });

      setTimeout(() => {
        setCopiedLink(null);
      }, 2000);
    } catch (error) {
      notify("warning", "Failed to copy enrollment link");
    }
  };

  return (
    <>
      <AdminHeader />

      <Container fluid className="mt--6 course-layout">
        <Row>
          <Col md="12">
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div className="d-flex align-items-center text-white ml-1">
                <span className="font-weight-bold small-text">
                  {showArtifacts ? "Artifact Details" : "Batch Details"}
                </span>
              </div>
            </div>
            {/* Page Header */}
            {showArtifacts ? (
              <DetailsHeader
                onBack={handleBackFromArtifacts}
                backTooltip="Back to Enrollment"
                title={assessmentArtifactListData?.data?.courseName || "Course Artifacts"}
                showProfile={true}
                profileImage={assessmentArtifactListData?.data?.traineeProfilePath}
                onProfileClick={() => setShowImageModal(true)}
                showEdit={false}
                chips={[
                  {
                    id: "batchNameChip",
                    icon: "fa fa-calendar-check",
                    name: "Batch",
                    value: assessmentArtifactListData?.data?.batchName,
                    tooltip: assessmentArtifactListData?.data?.batchName,
                  },
                  {
                    id: "traineeNameChip",
                    icon: "fa fa-user",
                    name: "Trainee",
                    value: assessmentArtifactListData?.data?.traineeName,
                    tooltip: assessmentArtifactListData?.data?.traineeName,
                  },
                  {
                    id: "traineeEmailChip",
                    icon: "fa fa-envelope",
                    name: "Email",
                    value: assessmentArtifactListData?.data?.traineeEmail,
                    tooltip: assessmentArtifactListData?.data?.traineeEmail,
                  },
                  {
                    id: "traineeMobileChip",
                    icon: "fa fa-phone",
                    name: "Mobile",
                    value: assessmentArtifactListData?.data?.traineeMobile,
                    tooltip: assessmentArtifactListData?.data?.traineeMobile,
                  },
                ].filter((chip) => chip.value)}
              />
            ) : (
              <DetailsHeader
                onBack={handleBack}
                backTooltip="Back to Batches"
                title={batchDetailData?.name || "Batch Details"}
                onEdit={handleEdit}
                editLabel="Edit"
                editTooltip="Edit Batch"
                editIcon="fa fa-pencil"
                showEdit={
                  !showAlternative.loader &&
                  !showAlternative.error &&
                  batchDetailData?.status !== "CANCELLED" &&
                  !isBatchStarted &&
                  !hideForTrainer
                }
              />
            )}

            {showAlternative.loader ? (
              <Card className="mt-3 details-state-card">
                <CardBody className="details-state-body">
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                </CardBody>
              </Card>
            ) : showAlternative.error ? (
              <Card className="mt-3 details-state-card">
                <CardBody className="details-state-body">
                  <div className="loaderContainer-sm error-style">
                    <div className="error-icon">
                      <i class="fa-solid fa-circle-exclamation"></i>
                    </div>
                    <span>{showAlternative.error}</span>
                  </div>
                </CardBody>
              </Card>
            ) : showArtifacts ? (
              <CourseArtifacts
                isEmbedded={true}
                embeddedCourseId={
                  batchDetailData?.courseId
                    ? urlEncodedId(batchDetailData.courseId)
                    : batchDetailData?.courses?.ID
                      ? urlEncodedId(batchDetailData.courses.ID)
                      : batchDetailData?.courses?.id
                        ? urlEncodedId(batchDetailData.courses.id)
                        : null
                }
                embeddedBatchId={urlDecodedId(id)}
                embeddedTraineeID={
                  artifactProps?.traineeID || artifactProps?.traineeId
                }
              />
            ) : (
              <>
                <Card
                  className={
                    showEnrollmentTab
                      ? "main-tabs-card mt-3"
                      : "border-0 bg-transparent shadow-none mt-3"
                  }
                >
                  {showEnrollmentTab && (
                    <CardHeader className="pb-0 course-sticky-header">
                      <Nav className="custom-tabs cursor">
                        <NavItem>
                          <NavLink
                            className={activeTab === "details" ? "active" : ""}
                            onClick={() => toggleTab("details")}
                          >
                            Batch Details
                          </NavLink>
                        </NavItem>
                        <NavItem>
                          <NavLink
                            className={
                              activeTab === "enrollment" ? "active" : ""
                            }
                            onClick={() => toggleTab("enrollment")}
                          >
                            Enrollments
                          </NavLink>
                        </NavItem>
                      </Nav>
                    </CardHeader>
                  )}
                  <CardBody className={showEnrollmentTab ? "" : "p-0"}>
                    <TabContent
                      activeTab={activeTab}
                      className="course-details"
                    >
                      <TabPane tabId="details" className="course-details">
                        {/* Basic + Participant Information */}
                        <Row className="align-items-stretch mt-3">
                          <Col md="6" className="d-flex">
                            <Card className="section-cards w-100">
                              <CardHeader className="section-header">
                                <h4 className="mb-0 text-default section-title">
                                  Basic Information
                                </h4>
                              </CardHeader>

                              <CardBody>
                                <Row>
                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Batch Name
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.name || "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Batch Code
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.code || "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Course
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.courses?.courseName ||
                                          "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  {batchDetailData?.trainingCenters?.name && (
                                    <Col md="6">
                                      <div className="mb-3">
                                        <Label className="small-label text-muted mb-1">
                                          Training Center
                                        </Label>

                                        <div className="small-text text-wrapper">
                                          {batchDetailData.trainingCenters.name}
                                        </div>
                                      </div>
                                    </Col>
                                  )}

                                  {batchDetailData?.trainingCenters
                                    ?.address && (
                                      <Col md="6">
                                        <div className="mb-3">
                                          <Label className="small-label text-muted mb-1">
                                            Training Center Address
                                          </Label>

                                          <div className="small-text text-wrapper">
                                            {
                                              batchDetailData.trainingCenters
                                                .address
                                            }
                                          </div>
                                        </div>
                                      </Col>
                                    )}

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Batch Language
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {toTitleCase(
                                          batchDetailData?.batchLanguage,
                                        ) || "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Status
                                      </Label>

                                      <div>
                                        <Badge
                                          color={
                                            STATUS_BADGE_COLORS[
                                            batchDetailData?.status
                                            ] || "secondary"
                                          }
                                          className="badge-lg rounded-pill"
                                        >
                                          {" "}
                                          {batchDetailData?.status ===
                                            "INPROGRESS"
                                            ? "Ongoing"
                                            : batchDetailData?.status || "—"}
                                        </Badge>
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Enrollment Link
                                      </Label>

                                      <div className="d-flex align-items-center mb-1 text-wrapper">
                                        <span className="small-text mr-2">
                                          Trainee:
                                        </span>

                                        {copiedLink?.batchId === batchDetailData?.ID &&
                                          copiedLink?.type === "trainee" ? (
                                          <span className="text-success font-weight-bold small-text">
                                            <i className="fa fa-check mr-1" />
                                            Link Copied!
                                          </span>
                                        ) : (
                                          <Button
                                            color="link"
                                            className="p-0"
                                            disabled={!batchDetailData?.batchEnrollmentURLTrainee}
                                            onClick={(e) => {
                                              e.stopPropagation();

                                              handleCopyEnrollmentLink(
                                                batchDetailData?.batchEnrollmentURLTrainee,
                                                batchDetailData?.ID,
                                                "trainee"
                                              );
                                            }}
                                            title="Copy Trainee Enrollment Link"
                                          >
                                            <i className="fa fa-link mr-1" />
                                            Copy Link
                                          </Button>
                                        )}
                                      </div>

                                      <div className="d-flex align-items-center text-wrapper">
                                        <span className="small-text mr-2">
                                          Channel Partner:
                                        </span>

                                        {copiedLink?.batchId === batchDetailData?.ID &&
                                          copiedLink?.type === "channelPartner" ? (
                                          <span className="text-success font-weight-bold small-text">
                                            <i className="fa fa-check mr-1" />
                                            Link Copied!
                                          </span>
                                        ) : (
                                          <Button
                                            color="link"
                                            className="p-0"
                                            disabled={!batchDetailData?.batchEnrollmentURLChannelPartner}
                                            onClick={(e) => {
                                              e.stopPropagation();

                                              handleCopyEnrollmentLink(
                                                batchDetailData?.batchEnrollmentURLChannelPartner,
                                                batchDetailData?.ID,
                                                "channelPartner"
                                              );
                                            }}
                                            title="Copy Channel Partner Enrollment Link"
                                          >
                                            <i className="fa fa-link mr-1" />
                                            Copy Link
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>

                          <Col md="6" className="d-flex">
                            <Card className="section-cards w-100">
                              <CardHeader className="section-header">
                                <h4 className="mb-0 text-default section-title">
                                  Schedule Information
                                </h4>
                              </CardHeader>

                              <CardBody>
                                <Row>
                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Registration Start
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {formatDate(
                                          batchDetailData?.registrationStartDate,
                                        )}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Registration End
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {formatDate(
                                          batchDetailData?.registrationEndDate,
                                        )}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Batch Start Date
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {formatDate(
                                          batchDetailData?.batchStartDate,
                                        )}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Batch End Date
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {formatDate(
                                          batchDetailData?.batchEndDate,
                                        )}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Start Time
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.startTime || "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        End Time
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.endTime || "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Duration
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.duration || "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  {batchDetailData?.virtualMeetingUrl && (
                                    <Col md="6">
                                      <div className="mb-3">
                                        <Label className="small-label text-muted mb-1">
                                          Virtual Meeting
                                        </Label>

                                        <div className="small-text">
                                          <a
                                            href={
                                              batchDetailData.virtualMeetingUrl
                                            }
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="d-inline-flex align-items-center"
                                            style={{
                                              color: "#2c5282",
                                              textDecoration: "none",
                                              fontWeight: 500,
                                            }}
                                          >
                                            <i className="fa fa-video-camera mr-2" />
                                            Join Virtual Meeting
                                            <i className="fa fa-external-link ml-2" />
                                          </a>
                                        </div>
                                      </div>
                                    </Col>
                                  )}
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Schedule + Trainers */}
                        <Row className="align-items-stretch mt-1">
                          <Col md="6" className="d-flex">
                            <Card className="section-cards w-100">
                              <CardHeader className="section-header">
                                <h4 className="mb-0 text-default section-title">
                                  Participant Information
                                </h4>
                              </CardHeader>

                              <CardBody>
                                <Row>
                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Minimum Participants
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.minParticipants ??
                                          "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Maximum Participants
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.maxParticipants ??
                                          "—"}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Enrolled Users
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {batchDetailData?.enrolledUserCount ??
                                          0}
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md="6">
                                    <div className="mb-3">
                                      <Label className="small-label text-muted mb-1">
                                        Capacity Status
                                      </Label>

                                      <div className="small-text text-wrapper">
                                        {toTitleCase(
                                          batchDetailData?.capacityStatus,
                                        ) || "—"}
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </CardBody>
                            </Card>
                          </Col>

                          <Col md="6" className="d-flex">
                            <Card className="section-cards w-100">
                              <CardHeader className="section-header">
                                <h4 className="mb-0 text-default section-title">
                                  Instructors
                                </h4>
                              </CardHeader>

                              <CardBody>
                                {batchDetailData?.trainers?.length ? (
                                  <div className="trainers-list">
                                    <Row>
                                      {batchDetailData?.trainers?.map(
                                        (trainer, index) => (
                                          <Col md="6" key={trainer.id || index}>
                                            <div className="p-1 mb-2">
                                              <span className="text-muted mr-1">
                                                {index + 1}.
                                              </span>

                                              <span className="small-text">
                                                {`${trainer?.user?.firstName || ""} ${trainer?.user?.lastName || ""
                                                  }`.trim() || "—"}
                                              </span>
                                            </div>
                                          </Col>
                                        ),
                                      )}
                                    </Row>
                                  </div>
                                ) : (
                                  <div className="loaderContainer empty-style">
                                    <div className="empty-icon">
                                      <i class="fa-solid fa-circle-info"></i>
                                    </div>
                                    <span>No instructors assigned.</span>
                                  </div>
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>

                        {/* Additional Information */}
                        {batchDetailData?.cancellationRemark && (
                          <Row className="mt-1">
                            <Col md="12" className="d-flex">
                              <Card className="section-cards w-100">
                                <CardHeader className="section-header">
                                  <h4 className="mb-0 text-default section-title">
                                    Additional Information
                                  </h4>
                                </CardHeader>
                                <CardBody>
                                  <Row>
                                    <Col md="6">
                                      <div className="mb-3">
                                        <Label className="small-label text-muted mb-1">
                                          Cancellation Remark
                                        </Label>
                                        <div className="small-text text-wrapper">
                                          {batchDetailData?.cancellationRemark ||
                                            "—"}
                                        </div>
                                      </div>
                                    </Col>
                                  </Row>
                                </CardBody>
                              </Card>
                            </Col>
                          </Row>
                        )}

                        <Row className="mt-1">
                          {batchDetailData?.scheduleInfo && (
                            <Col md="12" className="d-flex">
                              <Card className="section-cards w-100">
                                <CardHeader className="section-header d-flex align-items-center">
                                  <h4 className="mb-0 text-default section-title">
                                    Schedule Decription
                                  </h4>
                                  <InfoPopover
                                    targetId="batchScheduleInfoDetails"
                                    title="Schedule Decription"
                                    content="The information provided here will be visible to the trainee."
                                  />
                                </CardHeader>
                                <CardBody>
                                  {renderExpandableDescription(
                                    batchDetailData?.scheduleInfo || "—",
                                    expandedSections.scheduleInfo,
                                    (val) =>
                                      setExpandedSections((prev) => ({
                                        ...prev,
                                        scheduleInfo: val,
                                      })),
                                    "small-text text-wrapper",
                                  )}
                                </CardBody>
                              </Card>
                            </Col>
                          )}
                          <Col md="12" className="d-flex">
                            <Card className="section-cards w-100">
                              <CardHeader className="section-header d-flex align-items-center">
                                <h4 className="mb-0 text-default section-title">
                                  Batch Description
                                </h4>
                                <InfoPopover
                                  targetId="batchDescriptionInfoDetails"
                                  title="Batch Description"
                                  content="This information will be displayed to the trainee as part of the batch details."
                                />
                              </CardHeader>
                              <CardBody>
                                {renderExpandableDescription(
                                  batchDetailData?.description || "—",
                                  expandedSections.description,
                                  (val) =>
                                    setExpandedSections((prev) => ({
                                      ...prev,
                                      description: val,
                                    })),
                                  "small-text text-wrapper",
                                )}
                              </CardBody>
                            </Card>
                          </Col>
                        </Row>
                      </TabPane>
                      <TabPane tabId="enrollment">
                        {activeTab === "enrollment" && (
                          <EnrolledStudentsTab
                            courseName={batchDetailData?.courses?.courseName}
                            courseID={
                              batchDetailData?.courses?.ID
                                ? urlEncodedId(batchDetailData.courses.ID)
                                : null
                            }
                            batchId={
                              batchDetailData?.ID
                                ? urlEncodedId(batchDetailData.ID)
                                : null
                            }
                            onViewArtifacts={handleViewArtifacts}
                            accessType={batchDetailData?.courses?.accessType}
                          />
                        )}
                      </TabPane>
                    </TabContent>
                  </CardBody>
                </Card>
              </>
            )}
          </Col>
        </Row>
      </Container>
      {showImageModal && assessmentArtifactListData?.data?.traineeProfilePath && (
        <div
          className="image-modal-overlay"
          onClick={() => setShowImageModal(false)}
        >
          <div className="image-modal-content">
            <img src={assessmentArtifactListData?.data?.traineeProfilePath} alt="trainee-thumbnail" />
          </div>
        </div>
      )}
    </>
  );
}

export default BatchDetails;
